import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import './contact.css';

const quickResponses = [
  { 
    question: "What services do you offer?", 
    answer: "We provide fullstack development, blockchain solutions, SEO/digital marketing, and professional live streaming services. <a href='#services' class='chat-link'>View our Services</a>" 
  },
  { 
    question: "How can I get a quote?", 
    answer: "Please share your project details in the form and we'll respond with a customized quote within 24 hours. <a href='#contact' class='chat-link'>Go to contact form</a>" 
  },
  { 
    question: "Do you offer ongoing support?", 
    answer: "Yes! We provide maintenance packages starting at $99/month. <a href='mailto:globepawaventures@gmail.com' class='chat-link'>Email our support team</a>" 
  },
  { 
    question: "What's your response time?", 
    answer: "We typically respond to inquiries within 2 business hours during our working hours (9AM-5PM GMT+3). For urgent matters, call <a href='tel:+254 724 899116' class='chat-link'>+254 724 899116</a>" 
  }
];

export default function Contact() {
 // const {  formState: { errors, isSubmitting } } = useForm();
  const [isChatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const chatEndRef = useRef(null);
  const formRef = useRef(null);

  
  const handleQuickResponse = (response) => {
    // Add user question
    setMessages(prev => [...prev, { 
      text: response.question, 
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
    
    // Simulate bot typing
    setIsBotTyping(true);
    
    // Add bot response after delay
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        text: response.answer, 
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
      setIsBotTyping(false);
      scrollToBottom();
    }, 800);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    // Add user message
    setMessages(prev => [...prev, { 
      text: inputMessage, 
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
    setInputMessage('');
    
    // Simulate bot typing
    setIsBotTyping(true);
    
    // Simulate bot response after delay
    setTimeout(() => {
      const botResponse = "Thanks for your message! Our team will get back to you shortly. In the meantime, you might find answers in our <a href='#faq' class='chat-link'>FAQ section</a> or by submitting the contact form below.";
      setMessages(prev => [...prev, { 
        text: botResponse, 
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
      setIsBotTyping(false);
      scrollToBottom();
    }, 1500);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    if (isChatOpen) {
      scrollToBottom();
    }
  }, [isChatOpen, messages]);

  // Auto-focus input when chat opens
  useEffect(() => {
    if (isChatOpen) {
      const input = document.querySelector('.chat-input input');
      setTimeout(() => input?.focus(), 300);
    }
  }, [isChatOpen]);

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const chatWidget = {
    hidden: { scale: 0.8, opacity: 0, y: 20 },
    visible: { 
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 20 }
    },
    exit: { scale: 0.8, opacity: 0, y: 20 }
  };

  return (
    <motion.section 
      className="contact-section" 
      id="contacts"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={container}
      ref={formRef}
    >
      {/* Floating Chat Button */}
      <motion.button
        className="chat-button"
        onClick={() => setChatOpen(!isChatOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        aria-label="Open chat"
      >
        {isChatOpen ? (
          <span className="chat-icon">✕</span>
        ) : (
          <>
            <span className="chat-icon">💬</span>
            <span className="pulse-effect"></span>
          </>
        )}
      </motion.button>

      {/* Chat Widget */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            className="chat-widget"
            variants={chatWidget}
            initial="hidden"
            animate="visible"
            exit="exit"
            aria-modal="true"
            aria-labelledby="chat-header"
          >
            <div className="chat-header">
              <h3 id="chat-header">Globepawa AI Assistant</h3>
              <button 
                onClick={() => setChatOpen(false)}
                aria-label="Close chat"
              >
                ✕
              </button>
            </div>
            
            <div className="chat-messages">
              <div className="bot-message welcome-message">
                <p>Hi there! 👋 I'm your virtual assistant. How can I help you today?</p>
                <span className="message-time">Just now</span>
              </div>
              
              {messages.map((msg, index) => (
                <div 
                  key={index} 
                  className={`${msg.sender}-message`}
                  aria-live={msg.sender === 'bot' ? "polite" : "off"}
                >
                  <p dangerouslySetInnerHTML={{ __html: msg.text }}></p>
                  <span className="message-time">{msg.timestamp}</span>
                </div>
              ))}
              
              {isBotTyping && (
                <div className="bot-message typing-indicator">
                  <div className="typing-dots">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} aria-hidden="true" />
            </div>
            
            <div className="quick-responses">
  <h4>Quick Questions:</h4>
  <div className="quick-response-list">
    {quickResponses.map((response, index) => (
      <button
        key={index}
        onClick={() => handleQuickResponse(response)}
        className="quick-response"
      >
        {response.question}
      </button>
    ))}
  </div>
</div>

            
            <form onSubmit={handleSendMessage} className="chat-input">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                aria-label="Type your message"
              />
              <button 
                type="submit"
                disabled={!inputMessage.trim()}
                aria-label="Send message"
              >
                Send
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Form Section */}
      <motion.div className="contact-header" variants={item}>
        <h2>Get In Touch</h2>
        <p className="subtitle">Have a project or question? Let's create something amazing together.</p>
      </motion.div>

   
      <motion.div className="contact-info" variants={item}>
        <div className="info-item">
          <div className="info-icon">✉️</div>
          <a href="mailto:globepawaventures@gmail.com">globepawaventures@gmail.com</a>
        </div>
        <div className="info-item">
          <div className="info-icon">📱</div>
          <a href="tel:+254724899116">+254 724 899116</a>
        </div>
      </motion.div>
    </motion.section>
  );
}